import { ApolloClient, HttpLink, InMemoryCache } from "apollo-boost"
import { ApolloLink } from "apollo-link"
import { RetryLink } from "apollo-link-retry"
import { setContext } from "apollo-link-context"
import { onError } from "apollo-link-error"
import { Maybe } from "fgr-lib-business-logic/lib/common/types"
import { Router as RouterProps } from "next/router"
import { ResponseErrorType } from "../../../graphql/generated-types"
import logger from "../../../server/logger"
import { publicEnvVars } from "../../../utils/config"
import { isBrowser } from "../../../utils/general"
import { getTableOrderingId } from "../../../utils/url"
import { AuthProvider } from "../../context/AuthContext"
import { currentAuthenticatedUser, signOut } from "../../screens/auth/cognito"
import { hasSpecificResponseError, parseBaseTenantUrlFromUrl, shouldRetryRequest } from "./utils"

let apolloClient = null

const getButterAuthToken = (cognitoUser: Maybe<any>) => {
    if (!cognitoUser) {
        return null
    }
    if (cognitoUser.attributes) {
        return `c/${cognitoUser.signInUserSession.idToken.jwtToken}`
    } else if (cognitoUser.provider === AuthProvider.Facebook) {
        return `f/${cognitoUser.accessToken}`
    } else if (cognitoUser.provider === AuthProvider.Google) {
        return `g/${cognitoUser.accessToken}`
    } else {
        return null
    }
}

async function create(initialState, router: RouterProps) {
    const httpLink = new HttpLink({
        uri: publicEnvVars.GRAPHQL_SERVER_URL,
    })
    const errorLink = onError((error) => {
        if (error.graphQLErrors) {
            error.graphQLErrors.forEach(({ message, locations, path }) => {
                logger.error(`[GraphQL Error]: Message: ${message}, Location: ${locations}, Path: ${path}`, {
                    error: error,
                })
            })
        }
        if (error.networkError) {
            logger.error(`[GraphQL Network error]: ${error.networkError}`, { error: error })
        }

        // Check authentication validity
        if (hasSpecificResponseError(error, ResponseErrorType.InvalidAuth)) {
            signOut().then(() => {
                if (isBrowser) {
                    location.reload()
                }
            })
        }
    })
    const headersLink = setContext((_, { headers }) => {
        const tableId = getTableOrderingId(router)
        const tenantUrl = isBrowser ? parseBaseTenantUrlFromUrl(window.location.href) : null

        const customHeaders = {
            ...(tableId ? { "butter-table-id": tableId } : null),
            ...(tenantUrl ? { "x-butter-tenant-url": tenantUrl } : null),
        }

        return currentAuthenticatedUser()
            .then((user) => {
                const token = getButterAuthToken(user)

                return {
                    headers: {
                        ...headers,
                        ...customHeaders,
                        ...(token ? { "butter-auth-token": token } : null),
                    },
                }
            })
            .catch(() => {
                return {
                    headers: { ...headers, ...customHeaders },
                }
            })
    })

    const retryLink = new RetryLink({
        delay: { initial: 500 },
        attempts: {
            max: 5,
            retryIf: shouldRetryRequest,
        },
    })

    const link = ApolloLink.from([retryLink, errorLink, headersLink, httpLink])

    const cache = new InMemoryCache({}).restore(initialState || {})

    return new ApolloClient({
        connectToDevTools: isBrowser,
        ssrMode: !isBrowser, // Disables forceFetch on the server (so queries are only run once)
        link: link,
        cache: cache,
    })
}

export default async function initApollo(router: RouterProps, initialState?: any): Promise<ApolloClient<any>> {
    // Make sure to create a new client for every server-side request so that data
    // isn't shared between connections (which would be bad)
    if (!isBrowser) {
        return await create(initialState, router)
    }

    // Reuse client on the client-side
    if (!apolloClient) {
        apolloClient = await create(initialState, router)
    }

    return apolloClient
}
